import React, { useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "@fluentui/react-components";
import type { IContact } from "../../../../types/Contacts";
import { PersonAvatar, fullName } from "../Shared/Teamsshared";
import styles from "../Internalmessage.module.css";
import own from "./Speeddial.module.css";

interface Props {
  /** people that can still be added (everyone not already on the speed dial) */
  contacts: IContact[];
  onAdd: (c: IContact) => void;
  onClose: () => void;
  /** asks the directory to search the server for people beyond the first page */
  onQuery?: ((q: string) => Promise<void> | void) | undefined;
}

/** Highlights the part of `text` that matches the typed letters. */
const Marked: React.FC<{ text: string; q: string }> = ({ text, q }) => {
  const i = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <span className={own.mark}>{text.slice(i, i + q.length)}</span>
      {text.slice(i + q.length)}
    </>
  );
};

/** "Add to speed dial": type a name, pick someone from the list, press Add. */
const SpeedDialAdd: React.FC<Props> = ({ contacts, onAdd, onClose, onQuery }) => {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<IContact | null>(null);
  const [open, setOpen] = useState(true);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const seq = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const term = q.trim().toLowerCase();
  const shown = useMemo(
    () =>
      contacts
        .filter((c) => !term || `${fullName(c)} ${c.email} ${c.phoneNumber}`.toLowerCase().indexOf(term) !== -1)
        .slice()
        .sort((a, b) => fullName(a).localeCompare(fullName(b)))
        .slice(0, 40),
    [contacts, term],
  );

  useEffect(() => inputRef.current?.focus(), []);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  /* the list only appears once something is typed: a loader first, then the people found */
  const search = (value: string) => {
    window.clearTimeout(timer.current);
    const mine = ++seq.current;
    if (!value.trim()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    timer.current = window.setTimeout(async () => {
      try {
        if (value.trim().length >= 2) await onQuery?.(value.trim());
      } catch {
        /* the people already loaded are still listed */
      }
      if (mine === seq.current) setLoading(false);
    }, 300);
  };
  const listOpen = open && term.length > 0 && !picked;
  useEffect(() => setActive(0), [term]);
  useEffect(() => {
    const el = listRef.current?.children[active] as HTMLElement | undefined;
    el?.scrollIntoView?.({ block: "nearest" });
  }, [active]);

  const choose = (c: IContact) => {
    setPicked(c);
    window.clearTimeout(timer.current);
    seq.current++;
    setLoading(false);
    setQ(fullName(c));
    setOpen(false);
  };

  const onKey = (e: React.KeyboardEvent) => {
    e.stopPropagation(); // keep Escape from closing the whole messaging window
    if (e.key === "Escape") {
      if (listOpen) setOpen(false);
      else onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, Math.max(shown.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (listOpen) {
        const c = shown[active];
        if (c && !loading) choose(c);
      } else if (picked) onAdd(picked);
    }
  };

  return (
    <div className={own.wrap} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={own.dlg} role="dialog" aria-modal="true" aria-labelledby="sd-title" onKeyDown={onKey}>
        <h2 id="sd-title" className={own.title}>
          Add to speed dial
        </h2>
        <div className={own.combo}>
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded={listOpen}
            aria-controls="sd-list"
            aria-autocomplete="list"
            value={q}
            placeholder="Type a name or number"
                        onChange={(e) => {
              setQ(e.target.value);
              setPicked(null);
              setOpen(true);
              search(e.target.value);
            }}
          />
          {listOpen && (
            <div id="sd-list" ref={listRef} className={own.list} role="listbox" aria-label="People">
              {loading && (
                <div className={own.none} role="status">
                  <Spinner size="tiny" label="Searching…" labelPosition="after" />
                </div>
              )}
              {!loading && shown.length === 0 && <div className={own.none}>No people found</div>}
              {!loading && shown.map((c, i) => (
                <button key={c._id} type="button" role="option" aria-selected={i === active} className={`${own.opt} ${i === active ? own.optOn : ""}`} onMouseEnter={() => setActive(i)} onClick={() => choose(c)}>
                  <PersonAvatar name={fullName(c)} image={c.profilePicture} size={40} />
                  <span className={own.optText}>
                    <span className={own.optName}>
                      <Marked text={fullName(c)} q={term} />
                    </span>
                    <span className={own.optSub}>
                      <Marked text={c.email} q={term} />
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={own.foot}>
          <button type="button" className={styles.outlineBtn} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={styles.primaryBtn} disabled={!picked} onClick={() => picked && onAdd(picked)}>
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpeedDialAdd;